import { useMemo, type JSX } from 'react';
import { DrilldownButton } from '~/components/button'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { AnalyticsQueryFilterContext } from '~/modules/analytics/utils'

import { AnalyticsQueryAggregate, AnalyticsQueryFilter } from '@hap/analytics'
import { cn } from '@hap/components'
import {
  SCALE_3_POINT,
  SCALE_4_POINT,
  SCALE_5_POINT,
  SCALE_BINARY,
  ScorecardScale,
} from '@hap/constants'
import { Timespan, commafy, toPercent } from '@hap/utils'

export function ScoreBreakdown(
  props: AnalyticsQueryFilterContext & { scale: ScorecardScale },
): JSX.Element {
  const aggregations = useMemo<Record<string, AnalyticsQueryAggregate>>(() => {
    return Object.keys(RATING[props.scale].buckets).reduce(
      (acc, key) => ({
        ...acc,
        [`${key}`]: {
          op: 'count',
          field: 'id',
          filter: [{ field: 'awardedPoints', op: 'eq', value: key }],
        },
      }),
      {
        total: {
          op: 'count',
          field: 'id',
        },
        na: {
          op: 'count',
          field: 'id',
          filter: [{ field: 'awardedPoints', op: 'isNull' }],
        },
      },
    )
  }, [props.scale])
  const { data } = useAnalytics({
    timespan: props.timespan,
    filters: props.filters,
    aggregations,
    dataset: 'evaluationCriteriaScores',
  })

  return (
    <div>
      {Object.entries(RATING[props.scale].buckets).map(([key, bucket]) => {
        return (
          <div
            className="hover:bg-secondary grid grid-cols-[50px_1fr_auto] items-center gap-2 rounded-md p-2"
            key={key}
          >
            <div className="text-center text-2xl">{bucket.icon}</div>
            <div>{renderBar(key, bucket.color)}</div>
            <div>
              <DrilldownButton
                filters={[
                  ...props.filters.map((filter) => {
                    if (filter.field === 'publishedAt') {
                      return {
                        dataset: 'interactions',
                        field: 'averageEvaluationScore',
                        op: 'isNotNull',
                      } satisfies AnalyticsQueryFilter
                    }
                    if (filter.field === 'scorecardCriteriaId') {
                      return {
                        ...filter,
                        compound: [
                          {
                            dataset: 'evaluationCriteriaScores',
                            field: 'awardedPoints',
                            op: 'in',
                            values: [key],
                          },
                        ],
                      }
                    }
                    return filter
                  }),
                ]}
                timespan={props.timespan as Timespan.StaticTimespan}
              />
            </div>
          </div>
        )
      })}
      <div className="hover:bg-secondary grid grid-cols-[50px_1fr_auto] items-center gap-2 rounded-md p-2">
        <div className="text-center text-sm font-bold">N/A</div>
        <div>{renderBar('na', 'bg-foreground/10')}</div>
        <div>
          <DrilldownButton
            filters={props.filters.map((filter) => {
              if (filter.field === 'publishedAt') {
                return {
                  dataset: 'interactions',
                  field: 'averageEvaluationScore' as const,
                  op: 'isNotNull',
                }
              }
              if (filter.field === 'scorecardCriteriaId') {
                return {
                  ...filter,
                  compound: [
                    {
                      dataset: 'evaluationCriteriaScores',
                      field: 'awardedPoints',
                      op: 'in',
                      values: ['{{NULL}}'],
                    },
                  ],
                }
              }
              return filter
            })}
            timespan={props.timespan as Timespan.StaticTimespan}
          />
        </div>
      </div>
    </div>
  )

  function renderBar(key: string, color: string): JSX.Element {
    const value = data ? parseInt(data[0][key]) : 0
    const total = data ? parseInt(data[0]['total']) : 0
    const percent = toPercent(value / total, 0, 0)
    return (
      <div
        className={cn(
          'bg-accent flex gap-2 whitespace-nowrap rounded-md p-2 font-mono transition-all',
          color,
        )}
        style={{ width: percent }}
      >
        {percent} <span className="text-foreground/40">{commafy(value)}</span>
      </div>
    )
  }
}

const RATING = {
  [SCALE_BINARY]: {
    buckets: {
      '1': { icon: '👍', color: 'bg-green/50' },
      '0': { icon: '👎', color: 'bg-foreground/10' },
    },
  },
  [SCALE_3_POINT]: {
    buckets: {
      '2': { icon: '😄', color: 'bg-green/50' },
      '1': { icon: '😐', color: 'bg-green/20' },
      '0': { icon: '🙁', color: 'bg-foreground/10' },
    },
  },
  [SCALE_4_POINT]: {
    buckets: {
      '+2': { icon: '🤩', color: 'bg-green/50' },
      '+1': { icon: '😄', color: 'bg-green/30' },
      '-1': { icon: '😕', color: 'bg-green/20' },
      '-2': { icon: '😠', color: 'bg-foreground/10' },
    },
  },
  [SCALE_5_POINT]: {
    buckets: {
      '4': { icon: '🤩', color: 'bg-green/50' },
      '3': { icon: '😄', color: 'bg-green/30' },
      '2': { icon: '😐', color: 'bg-green/20' },
      '1': { icon: '😕', color: 'bg-green/10' },
      '0': { icon: '😠', color: 'bg-foreground/10' },
    },
  },
} satisfies Record<
  ScorecardScale,
  {
    buckets: Record<
      string,
      {
        icon: string
        color: string
      }
    >
  }
>
